import { shouldShowError } from "./api.js";

/**
 * Error debounce tracking to prevent duplicate error notifications
 */
let lastErrorTime = 0;
let lastErrorMessage = '';
const ERROR_DEBOUNCE_MS = 5000; // Don't show same error within 5 seconds

/**
 * Check if error notification should be debounced
 * @param {string} errorMessage - The error message to check
 * @returns {boolean} True if error should be debounced
 */
const shouldDebounceError = (errorMessage) => {
    const now = Date.now();
    const isDuplicate = errorMessage === lastErrorMessage && (now - lastErrorTime) < ERROR_DEBOUNCE_MS;
    
    if (!isDuplicate) {
        lastErrorTime = now;
        lastErrorMessage = errorMessage;
    }
    
    // Reset error tracking after debounce period
    if (now - lastErrorTime >= ERROR_DEBOUNCE_MS) {
        lastErrorMessage = '';
    }
    
    return isDuplicate;
};

/**
 * Handle API errors with consistent error handling
 * @param {Error} error - The error object
 * @param {Object} options - Error handling options
 * @param {string} options.defaultMessage - Default error message if no specific message found
 * @param {boolean} options.showToast - Whether to show toast notification (default: true)
 * @param {Function} options.onError - Custom error handler callback
 * @returns {Object} Error information object
 */
export const handleApiError = (error, options = {}) => {
    const {
        defaultMessage = "Error: 3001: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ",
        onError = null
    } = options;

    // Log error for debugging
    console.error("API Error:", error.message || error);

    // Extract error message
    let errorMessage = defaultMessage;
    
    if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
    } else if (error.message) {
        errorMessage = error.message;
    }

    // Check if error should be shown
    shouldShowError(error);
    
    // Check if error should be debounced
    shouldDebounceError(errorMessage);

    // Toast notifications removed - components should handle their own toasts
    // if (showToast && shouldShow && !isDebounced) {
    //     toast.error(errorMessage);
    // }

    // Call custom error handler if provided
    if (onError && typeof onError === 'function') {
        onError(error, errorMessage);
    }

    return {
        error,
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
    };
};

/**
 * Handle API errors silently (no toast notification)
 * @param {Error} error - The error object
 * @returns {Object} Error information object
 */
export const handleApiErrorSilently = (error) => {
    return handleApiError(error, { showToast: false });
};

